import { DomainError } from '../errors.js';
import {
  RA_ASSESSMENT_TYPES,
  RA_CONTROL_HIERARCHY,
  RA_VESSEL_GROUPS,
  type RaAssessmentType,
  type RaControlHierarchy,
  type RaControlStage,
  type RaMasterStatus,
  type RaRowControlStatus,
  type RaVesselGroup,
  raLinkIssues,
  raMasterKey,
} from './master.js';
import {
  LEGACY_RISK_MATRIX,
  type RaBandKey,
  type RaMatrixBand,
  type RaMatrixDefinition,
  raMatrixIssues,
  raMatrixSchema,
  STANDARD_RISK_MATRIX,
} from './matrix.js';

/**
 * Risk kütüphanesi Excel'inin ayrıştırılması (K2; ADR 0086 §5; README §3.3, XL START HERE ve
 * 09 Import Validation). Saf adımdır: sayfaları (ilk satır başlık, sütun başlık adıyla) satır
 * verisine çevirir, satır hatalarını ve uyarılarını toplar. Yazmaz; var olan ana veriyle
 * karşılaştırma ve yazma `@felix/db`'dedir (kod doğal anahtar). Hatalı satır veriye girmez; hata
 * varken içe aktarma hiçbir şey yazmaz.
 *
 * Sayfalar: 01 Areas, 02 Activities, 03 Hazard Master, 04 Control Library zorunlu; 05 RA Import
 * ve 06 Relational Links birlikte (faaliyet–tehlike bağı); 07 Matrix & Rules ve 08 Vessel Types
 * isteğe bağlı (denetim). START HERE'deki sayılar uyarıdır.
 */

export type RaLibraryCell = string | number | null;

export interface RaLibrarySheets {
  guide: RaLibraryCell[][] | null;
  areas: RaLibraryCell[][] | null;
  activities: RaLibraryCell[][] | null;
  hazards: RaLibraryCell[][] | null;
  controls: RaLibraryCell[][] | null;
  rows: RaLibraryCell[][] | null;
  links: RaLibraryCell[][] | null;
  matrix: RaLibraryCell[][] | null;
  vesselTypes: RaLibraryCell[][] | null;
}
export type RaLibrarySheet = keyof RaLibrarySheets;

/** Ortağın dosyasındaki sayfa adları. */
export const RA_LIBRARY_SHEET_NAMES: Readonly<Record<RaLibrarySheet, string>> = {
  guide: 'START HERE',
  areas: '01 Areas',
  activities: '02 Activities',
  hazards: '03 Hazard Master',
  controls: '04 Control Library',
  rows: '05 RA Import',
  links: '06 Relational Links',
  matrix: '07 Matrix & Rules',
  vesselTypes: '08 Vessel Types',
};

/**
 * FINAL teslim paketinin sayfaları (ADR 0098 §1): sorun raporunda v1 sayfalarıyla aynı listede
 * görünür. Ayrıştırma `library-final.ts`'tedir.
 */
export const RA_FINAL_SHEET_NAMES = {
  final00: 'FINAL 00 READ ME',
  final01: 'FINAL 01 Activity Import',
  final02: 'FINAL 02 Risk Import',
  final03: 'FINAL 03 Decision Queue',
} as const;
export type RaFinalSheet = keyof typeof RA_FINAL_SHEET_NAMES;

/** İçe aktarma raporunun sayfası: v1 ya da FINAL. */
export type RaImportSheet = RaLibrarySheet | RaFinalSheet;

/** Rapordaki sayfa anahtarının dosyadaki adı (worker çıktısı). */
export const RA_IMPORT_SHEET_NAMES: Readonly<Record<RaImportSheet, string>> = {
  ...RA_LIBRARY_SHEET_NAMES,
  ...RA_FINAL_SHEET_NAMES,
};

interface Column {
  key: string;
  header: string;
  required: boolean;
}

const col = (key: string, header: string, required = true): Column => ({ key, header, required });

/** Sütun başlıkları; `required` olmayan sütun yoksa boş sayılır. */
export const RA_LIBRARY_COLUMNS: Readonly<Record<RaLibrarySheet, readonly Column[]>> = {
  guide: [col('topic', 'Topic'), col('text', 'Instructions')],
  areas: [
    col('code', 'Area ID'),
    col('name', 'Operation Area'),
    col('vesselGroups', 'Vessel Applicability'),
    col('status', 'Company Status', false),
  ],
  activities: [
    col('code', 'Activity ID'),
    col('area', 'Operation Area'),
    col('name', 'Activity'),
    col('scope', 'Task / Work Scope'),
    col('vesselGroups', 'Vessel Applicability'),
    col('status', 'Status', false),
    col('raType', 'RA Type', false),
    col('owner', 'Owner', false),
  ],
  hazards: [
    col('code', 'Master ID'),
    col('family', 'Hazard Code'),
    col('name', 'Hazard'),
    col('consequence', 'Potential Consequence', false),
    col('recordType', 'Record Type', false),
  ],
  controls: [
    col('code', 'Control ID'),
    col('family', 'Hazard Code'),
    col('stage', 'Control Stage'),
    col('measure', 'Control Measure'),
    col('hierarchy', 'Control Hierarchy'),
  ],
  rows: [
    col('code', 'Risk Row ID'),
    col('activityCode', 'Activity ID'),
    col('area', 'Operation Area', false),
    col('activity', 'Activity', false),
    col('task', 'Task', false),
    col('family', 'Hazard Code'),
    col('hazard', 'Hazard', false),
    col('consequence', 'Potential Consequence', false),
    col('exposed', 'Exposed Persons', false),
    col('likelihood', 'Initial L'),
    col('severity', 'Initial S'),
    col('initialScore', 'Initial Score', false),
    col('existing', 'Existing Controls', false),
    col('additional', 'Additional Controls', false),
    col('hierarchy', 'Control Hierarchy', false),
    col('evidence', 'Evidence / Verification', false),
    col('controlStatus', 'Control Status', false),
    col('residualLikelihood', 'Residual L', false),
    col('residualSeverity', 'Residual S', false),
    col('residualScore', 'Residual Score', false),
    col('publication', 'Publication Status', false),
    col('vesselGroups', 'Vessel Applicability', false),
  ],
  links: [
    col('code', 'Risk Row ID'),
    col('family', 'Hazard Family', false),
    col('hazardCode', 'Master Hazard ID'),
    col('existingControlCode', 'Existing Control ID', false),
    col('additionalControlCode', 'Additional Control ID', false),
  ],
  matrix: [
    col('code', 'Code'),
    col('setting', 'Setting'),
    col('value', 'Value'),
    col('meaning', 'Meaning / rule', false),
  ],
  vesselTypes: [col('code', 'Code'), col('name', 'Vessel type', false)],
};

export type RaLibraryIssueCode =
  | 'REQUIRED'
  | 'TOO_LONG'
  | 'INVALID_CODE'
  | 'DUPLICATE_CODE'
  | 'UNKNOWN_VESSEL_GROUP'
  | 'INVALID_STATUS'
  | 'INVALID_RA_TYPE'
  | 'INVALID_STAGE'
  | 'INVALID_HIERARCHY'
  | 'INVALID_SCALE'
  | 'INVALID_CONTROL_STATUS'
  | 'SCORE_MISMATCH'
  | 'RESIDUAL_INCOMPLETE'
  | 'RESIDUAL_ABOVE_INITIAL'
  | 'RESIDUAL_NOT_VERIFIED'
  | 'UNKNOWN_HAZARD'
  | 'UNKNOWN_CATEGORY'
  | 'UNKNOWN_ACTIVITY'
  | 'UNKNOWN_CONTROL'
  | 'UNKNOWN_ROW'
  | 'LINK_MISSING'
  | 'LINK_MISMATCH'
  | 'CONTROL_STAGE_MISMATCH'
  | 'MATRIX_INVALID'
  | 'TEXT_MISMATCH'
  | 'APPLICABILITY_MISMATCH'
  | 'STEP_MISMATCH'
  | 'COUNT_MISMATCH'
  // FINAL paketi (ADR 0098)
  | 'PARENT_MISMATCH'
  | 'NAME_VARIANT'
  | 'UNRECOGNIZED_VALUE'
  | 'APPLICABILITY_REVIEW'
  | 'INITIAL_INCOMPLETE'
  | 'RESIDUAL_WITHOUT_INITIAL'
  | 'DUPLICATE_LINK'
  | 'INVALID_ISSUE_TYPE'
  | 'INVALID_DATE'
  | 'APPROVAL_IGNORED';

/** Satır hatası ya da uyarısı: sayfa, Excel satır numarası (başlık 1), alan, kod. */
export interface RaLibraryIssue {
  sheet: RaImportSheet;
  row: number;
  field: string;
  code: RaLibraryIssueCode;
}

export interface RaLibraryCategory {
  row: number;
  code: string;
  name: string;
  vesselGroups: RaVesselGroup[];
  status: RaMasterStatus;
}

export interface RaLibraryActivity {
  row: number;
  code: string;
  /** 02'de alan adıyla gelir (Operation Area); kategori adla bulunur. */
  categoryName: string;
  name: string;
  scope: string;
  vesselGroups: RaVesselGroup[];
  assessmentTypes: RaAssessmentType[];
  ownerRole: string | null;
  status: RaMasterStatus;
}

export interface RaLibraryHazard {
  row: number;
  code: string;
  family: string;
  name: string;
  consequences: string[];
}

export interface RaLibraryControl {
  row: number;
  code: string;
  hazardFamily: string;
  stage: RaControlStage;
  measure: string;
  hierarchy: RaControlHierarchy[];
}

export interface RaLibraryLink {
  row: number;
  code: string;
  activityCode: string;
  /** Ana tehlike kimliği (06 Master Hazard ID). */
  hazardCode: string;
  stepText: string | null;
  likelihood: number;
  severity: number;
  residualLikelihood: number | null;
  residualSeverity: number | null;
  existingControlCode: string | null;
  additionalControlCode: string | null;
  controlStatus: RaRowControlStatus;
  evidence: string | null;
  exposed: string | null;
}

export interface RaLibraryData {
  categories: RaLibraryCategory[];
  activities: RaLibraryActivity[];
  hazards: RaLibraryHazard[];
  /** Tekil sonuçlar (03'teki ";" ile ayrılmış parçalar; büyük/küçük harf duyarsız, ilk yazılış). */
  consequences: string[];
  controls: RaLibraryControl[];
  links: RaLibraryLink[];
  /** 07'deki bantlar (kabul seviyesi bant adından); sayfa ya da bant yoksa `null`. */
  matrix: RaMatrixDefinition | null;
  /** 08'deki gemi grupları; sayfa yoksa boş. */
  vesselGroups: RaVesselGroup[];
  errors: RaLibraryIssue[];
  warnings: RaLibraryIssue[];
}

/** Alan sınırları (ana veri şemasıyla aynı). */
export const RA_LIBRARY_LIMITS = {
  categoryName: 120,
  activityName: 200,
  scope: 2000,
  ownerRole: 120,
  hazardName: 300,
  consequence: 200,
  measure: 500,
  evidence: 500,
  exposed: 300,
} as const;

const CODE = /^[A-Z0-9][A-Z0-9._-]{0,39}$/;

export function cellText(value: RaLibraryCell | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).normalize('NFC').trim().replace(/\s+/g, ' ');
  return text === '' ? null : text;
}

const sameText = (a: string | null, b: string | null) =>
  (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

interface RowReader {
  row: number;
  text(key: string): string | null;
}

function headerKey(value: RaLibraryCell | undefined): string {
  return (cellText(value) ?? '').toLowerCase();
}

/** Sayfanın veri satırları; gerekli başlık yoksa `INVALID_HEADER` (ayrıntı sayfa ve başlık). */
function rowsOf(sheet: RaLibrarySheet, rows: RaLibraryCell[][]): RowReader[] {
  const header = rows[0] ?? [];
  const index = new Map<string, number>();
  header.forEach((cell, i) => {
    const key = headerKey(cell);
    if (key !== '' && !index.has(key)) index.set(key, i);
  });
  const columns = new Map<string, number>();
  for (const column of RA_LIBRARY_COLUMNS[sheet]) {
    const i = index.get(column.header.toLowerCase());
    if (i === undefined) {
      if (column.required) {
        const detail = `${RA_LIBRARY_SHEET_NAMES[sheet]}: ${column.header}`;
        throw new DomainError('INVALID_HEADER', detail, detail);
      }
      continue;
    }
    columns.set(column.key, i);
  }
  const readers: RowReader[] = [];
  rows.slice(1).forEach((cells, offset) => {
    if (!cells.some((c) => cellText(c) !== null)) return;
    readers.push({
      row: offset + 2,
      text(key) {
        const i = columns.get(key);
        return i === undefined ? null : cellText(cells[i]);
      },
    });
  });
  return readers;
}

function codeOf(value: string | null): string | null {
  return value === null ? null : raMasterKey(value);
}

/** "ALL", "CARGO, TANKER" ya da "CARGO / TANKER"; bilinmeyen grup `undefined`. */
export function groupsOf(value: string | null): RaVesselGroup[] | undefined {
  if (value === null) return undefined;
  const parts = value
    .split(/[,;/]/)
    .map((p) =>
      p
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '_'),
    )
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  const groups: RaVesselGroup[] = [];
  for (const part of parts) {
    if (!(RA_VESSEL_GROUPS as readonly string[]).includes(part)) return undefined;
    if (!groups.includes(part as RaVesselGroup)) groups.push(part as RaVesselGroup);
  }
  return groups.includes('ALL') ? ['ALL'] : groups;
}

/** "Draft", "Draft - company review" → taslak; "Active/Approved/Published" → etkin; boş → taslak. */
function statusOf(value: string | null): RaMasterStatus | undefined {
  if (value === null) return 'draft';
  const v = value.toLowerCase();
  if (v.startsWith('draft')) return 'draft';
  if (/^(active|approved|published)/.test(v)) return 'active';
  if (/^(retired|inactive|withdrawn)/.test(v)) return 'retired';
  return undefined;
}

const RA_TYPE_WORDS: Readonly<Record<string, RaAssessmentType>> = {
  routine: 'routine',
  'task-specific': 'task_specific',
  'task specific': 'task_specific',
  task_specific: 'task_specific',
  dynamic: 'dynamic',
  'change-related': 'change_related',
  'change related': 'change_related',
  change: 'change_related',
};

function raTypesOf(value: string | null): RaAssessmentType[] | undefined {
  if (value === null) return [];
  const types: RaAssessmentType[] = [];
  for (const part of value.split(/[,;/]/)) {
    const word = part.trim().toLowerCase();
    if (word === '') continue;
    const type = RA_TYPE_WORDS[word];
    if (!type) return undefined;
    if (!types.includes(type)) types.push(type);
  }
  return types.sort((a, b) => RA_ASSESSMENT_TYPES.indexOf(a) - RA_ASSESSMENT_TYPES.indexOf(b));
}

/** "Existing / baseline" → existing; "Additional / improvement" → additional. */
function stageOf(value: string | null): RaControlStage | undefined {
  const v = (value ?? '').toLowerCase();
  if (v.startsWith('existing') || v.startsWith('baseline')) return 'existing';
  if (v.startsWith('additional') || v.startsWith('improvement')) return 'additional';
  return undefined;
}

/** Bileşik hiyerarşi "Engineering / Administrative": sırasıyla; ilki birincil (README §3.3). */
function hierarchyOf(value: string | null): RaControlHierarchy[] | undefined {
  if (value === null) return undefined;
  const list: RaControlHierarchy[] = [];
  for (const part of value.split(/[,;/]/)) {
    const word = part.trim().toLowerCase();
    if (word === '') continue;
    const key = (
      word === 'eliminate' ? 'elimination' : word === 'substitute' ? 'substitution' : word
    ) as RaControlHierarchy;
    if (!RA_CONTROL_HIERARCHY.includes(key)) return undefined;
    if (!list.includes(key)) list.push(key);
  }
  return list.length > 0 ? list : undefined;
}

const CONTROL_STATUS_WORDS: Readonly<Record<string, RaRowControlStatus>> = {
  'not verified': 'not_verified',
  implemented: 'implemented',
  'not implemented': 'not_implemented',
  'n/a - reviewed': 'na_reviewed',
  'n/a – reviewed': 'na_reviewed',
  'n/a reviewed': 'na_reviewed',
};

export function controlStatusOf(value: string | null): RaRowControlStatus | undefined {
  if (value === null) return 'not_verified';
  return CONTROL_STATUS_WORDS[value.toLowerCase()];
}

/** 1–5 tam sayı; boş `null`; geçersiz `undefined`. */
export function scaleOf(value: string | null): number | null | undefined {
  if (value === null) return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : undefined;
}

/** 07'deki bant adı: "Low: ..." → low; "Very High" → very_high. */
function bandKeyOf(meaning: string | null): RaBandKey | undefined {
  const name = (meaning ?? '').split(':')[0]?.trim().toLowerCase().replace(/\s+/g, '_') ?? '';
  const keys: readonly string[] = ['very_low', 'low', 'medium', 'high', 'very_high', 'critical'];
  return keys.includes(name) ? (name as RaBandKey) : undefined;
}

/**
 * Bandın kabul seviyesi bant adından (ADR 0085 §2): ortağın 4 bandı ve Legacy 5-band'in aynı
 * adlı bandı. 07 kabul seviyesini yazmaz; bant sınırları dosyadan gelir.
 */
function acceptanceOf(key: RaBandKey): Pick<RaMatrixBand, 'acceptance' | 'acceptedBy'> {
  const band =
    STANDARD_RISK_MATRIX.bands.find((b) => b.key === key) ??
    LEGACY_RISK_MATRIX.bands.find((b) => b.key === key);
  return {
    acceptance: band?.acceptance ?? 'unacceptable',
    acceptedBy: [...(band?.acceptedBy ?? [])],
  };
}

function within(value: string | null, max: number): boolean {
  return value === null || value.length <= max;
}

/**
 * Sayfaları kütüphane verisine çevirir (ADR 0086 §5). Zorunlu sayfa yoksa `SHEET_MISSING`,
 * gerekli başlık yoksa `INVALID_HEADER` fırlatır; satır hataları ve uyarıları döner.
 */
export function parseRaLibrarySheets(sheets: RaLibrarySheets): RaLibraryData {
  for (const sheet of ['areas', 'activities', 'hazards', 'controls'] as const) {
    if (!sheets[sheet]) {
      const name = RA_LIBRARY_SHEET_NAMES[sheet];
      throw new DomainError('SHEET_MISSING', name, name);
    }
  }
  if (!sheets.rows !== !sheets.links) {
    const name = RA_LIBRARY_SHEET_NAMES[sheets.rows ? 'links' : 'rows'];
    throw new DomainError('SHEET_MISSING', name, name);
  }
  const read = (sheet: RaLibrarySheet) => {
    const rows = sheets[sheet];
    return rows ? rowsOf(sheet, rows) : [];
  };
  const guide = read('guide');
  const areaRows = read('areas');
  const activityRows = read('activities');
  const hazardRows = read('hazards');
  const controlRows = read('controls');
  const raRows = read('rows');
  const linkRows = read('links');
  const matrixRows = read('matrix');
  const typeRows = read('vesselTypes');

  const errors: RaLibraryIssue[] = [];
  const warnings: RaLibraryIssue[] = [];
  const data: RaLibraryData = {
    categories: [],
    activities: [],
    hazards: [],
    consequences: [],
    controls: [],
    links: [],
    matrix: null,
    vesselGroups: [],
    errors,
    warnings,
  };

  // START HERE: "11 areas / 146 activities / 442 risk rows / 39 hazard families" (uyarı)
  for (const r of guide) {
    if (!sameText(r.text('topic'), 'Counts')) continue;
    const text = (r.text('text') ?? '').toLowerCase();
    const expected: ReadonlyArray<readonly [string, RegExp, number]> = [
      ['areas', /(\d+)\s*areas/, areaRows.length],
      ['activities', /(\d+)\s*activities/, activityRows.length],
      ['rows', /(\d+)\s*risk rows/, raRows.length],
      ['hazards', /(\d+)\s*hazard/, hazardRows.length],
    ];
    for (const [field, pattern, actual] of expected) {
      const match = pattern.exec(text);
      if (match && Number(match[1]) !== actual) {
        warnings.push({ sheet: 'guide', row: r.row, field, code: 'COUNT_MISMATCH' });
      }
    }
  }

  // 08 Vessel Types
  const allowedGroups = new Set<string>(RA_VESSEL_GROUPS);
  if (typeRows.length > 0) {
    const listed = new Set<string>();
    for (const r of typeRows) {
      const code = codeOf(r.text('code'));
      if (code === null) {
        errors.push({ sheet: 'vesselTypes', row: r.row, field: 'code', code: 'REQUIRED' });
      } else if (!allowedGroups.has(code)) {
        errors.push({
          sheet: 'vesselTypes',
          row: r.row,
          field: 'code',
          code: 'UNKNOWN_VESSEL_GROUP',
        });
      } else if (!listed.has(code)) {
        listed.add(code);
        data.vesselGroups.push(code as RaVesselGroup);
      }
    }
  }
  /** Grup kapalı listede ve 08 varsa orada da. */
  const groupList = (value: string | null): RaVesselGroup[] | undefined => {
    const groups = groupsOf(value);
    if (!groups) return undefined;
    if (data.vesselGroups.length > 0 && groups.some((g) => !data.vesselGroups.includes(g))) {
      return undefined;
    }
    return groups;
  };

  type Sink = { error(row: number, field: string, code: RaLibraryIssueCode): void };
  const sinkFor = (sheet: RaLibrarySheet, failed: Set<number>): Sink => ({
    error(row, field, code) {
      failed.add(row);
      errors.push({ sheet, row, field, code });
    },
  });
  const warn = (sheet: RaLibrarySheet, row: number, field: string, code: RaLibraryIssueCode) =>
    warnings.push({ sheet, row, field, code });
  const checkCode = (
    sink: Sink,
    row: number,
    value: string | null,
    seen: Set<string>,
    field = 'code',
  ): string | null => {
    const code = codeOf(value);
    if (code === null) sink.error(row, field, 'REQUIRED');
    else if (!CODE.test(code)) sink.error(row, field, 'INVALID_CODE');
    else if (seen.has(code)) sink.error(row, field, 'DUPLICATE_CODE');
    if (code !== null) seen.add(code);
    return code;
  };
  const required = (sink: Sink, row: number, field: string, value: string | null, max: number) => {
    if (value === null) sink.error(row, field, 'REQUIRED');
    else if (value.length > max) sink.error(row, field, 'TOO_LONG');
    return value;
  };

  // 01 Areas → kategoriler
  {
    const failed = new Set<number>();
    const sink = sinkFor('areas', failed);
    const seen = new Set<string>();
    for (const r of areaRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      const name = required(sink, r.row, 'name', r.text('name'), RA_LIBRARY_LIMITS.categoryName);
      const groups = groupList(r.text('vesselGroups'));
      if (!groups) sink.error(r.row, 'vesselGroups', 'UNKNOWN_VESSEL_GROUP');
      const status = statusOf(r.text('status'));
      if (!status) sink.error(r.row, 'status', 'INVALID_STATUS');
      if (failed.has(r.row) || !code || !name || !groups || !status) continue;
      data.categories.push({ row: r.row, code, name, vesselGroups: groups, status });
    }
  }
  // 02 Activities → faaliyetler (kategori adla; dosyada yoksa var olan ana veride aranır)
  const activities = new Map<string, RaLibraryActivity>();
  {
    const failed = new Set<number>();
    const sink = sinkFor('activities', failed);
    const seen = new Set<string>();
    for (const r of activityRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      const area = required(sink, r.row, 'area', r.text('area'), RA_LIBRARY_LIMITS.categoryName);
      const name = required(sink, r.row, 'name', r.text('name'), RA_LIBRARY_LIMITS.activityName);
      const scope = required(sink, r.row, 'scope', r.text('scope'), 300);
      const groups = groupList(r.text('vesselGroups'));
      if (!groups) sink.error(r.row, 'vesselGroups', 'UNKNOWN_VESSEL_GROUP');
      const status = statusOf(r.text('status'));
      if (!status) sink.error(r.row, 'status', 'INVALID_STATUS');
      const types = raTypesOf(r.text('raType'));
      if (!types) sink.error(r.row, 'raType', 'INVALID_RA_TYPE');
      const owner = r.text('owner');
      if (!within(owner, RA_LIBRARY_LIMITS.ownerRole)) sink.error(r.row, 'owner', 'TOO_LONG');
      if (failed.has(r.row) || !code || !area || !name || !scope || !groups) continue;
      if (!status || !types) continue;
      const activity: RaLibraryActivity = {
        row: r.row,
        code,
        categoryName: area,
        name,
        scope,
        vesselGroups: groups,
        assessmentTypes: types,
        ownerRole: owner,
        status,
      };
      data.activities.push(activity);
      activities.set(code, activity);
    }
  }
  // 03 Hazard Master → tehlikeler ve tekil sonuçlar
  const hazardsByCode = new Map<string, RaLibraryHazard>();
  const hazardsByFamily = new Map<string, RaLibraryHazard>();
  {
    const failed = new Set<number>();
    const sink = sinkFor('hazards', failed);
    const seen = new Set<string>();
    const families = new Set<string>();
    const consequences = new Map<string, string>();
    for (const r of hazardRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      const family = checkCode(sink, r.row, r.text('family'), families, 'family');
      const name = required(sink, r.row, 'name', r.text('name'), RA_LIBRARY_LIMITS.hazardName);
      const parts = (r.text('consequence') ?? '')
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.some((p) => p.length > RA_LIBRARY_LIMITS.consequence)) {
        sink.error(r.row, 'consequence', 'TOO_LONG');
      }
      if (failed.has(r.row) || !code || !family || !name) continue;
      const unique: string[] = [];
      for (const part of parts) {
        const key = part.toLowerCase();
        if (!consequences.has(key)) consequences.set(key, part);
        const name = consequences.get(key) as string;
        if (!unique.includes(name)) unique.push(name);
      }
      const hazard: RaLibraryHazard = { row: r.row, code, family, name, consequences: unique };
      data.hazards.push(hazard);
      hazardsByCode.set(code, hazard);
      hazardsByFamily.set(family, hazard);
    }
    data.consequences = [...consequences.values()];
  }

  // 04 Control Library → kontroller
  const controlsByCode = new Map<string, RaLibraryControl>();
  {
    const failed = new Set<number>();
    const sink = sinkFor('controls', failed);
    const seen = new Set<string>();
    for (const r of controlRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      const family = codeOf(r.text('family'));
      if (family === null) sink.error(r.row, 'family', 'REQUIRED');
      else if (!hazardsByFamily.has(family)) sink.error(r.row, 'family', 'UNKNOWN_HAZARD');
      const stage = stageOf(r.text('stage'));
      if (!stage) sink.error(r.row, 'stage', 'INVALID_STAGE');
      const measure = required(
        sink,
        r.row,
        'measure',
        r.text('measure'),
        RA_LIBRARY_LIMITS.measure,
      );
      const hierarchy = hierarchyOf(r.text('hierarchy'));
      if (!hierarchy) sink.error(r.row, 'hierarchy', 'INVALID_HIERARCHY');
      if (failed.has(r.row) || !code || !family || !stage || !measure || !hierarchy) continue;
      const control: RaLibraryControl = {
        row: r.row,
        code,
        hazardFamily: family,
        stage,
        measure,
        hierarchy,
      };
      data.controls.push(control);
      controlsByCode.set(code, control);
    }
  }

  // 06 Relational Links: satır kimliğine göre ana tehlike ve iki kontrol
  interface Relation {
    row: number;
    hazardCode: string;
    existing: string | null;
    additional: string | null;
  }
  const relations = new Map<string, Relation>();
  const failedRelations = new Set<string>();
  {
    const failed = new Set<number>();
    const sink = sinkFor('links', failed);
    const seen = new Set<string>();
    for (const r of linkRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      const hazardCode = codeOf(r.text('hazardCode'));
      if (hazardCode === null) sink.error(r.row, 'hazardCode', 'REQUIRED');
      else if (!hazardsByCode.has(hazardCode)) sink.error(r.row, 'hazardCode', 'UNKNOWN_HAZARD');
      const existing = codeOf(r.text('existingControlCode'));
      const additional = codeOf(r.text('additionalControlCode'));
      for (const [field, value, stage] of [
        ['existingControlCode', existing, 'existing'],
        ['additionalControlCode', additional, 'additional'],
      ] as const) {
        if (value === null) continue;
        const control = controlsByCode.get(value);
        if (!control) sink.error(r.row, field, 'UNKNOWN_CONTROL');
        else if (control.stage !== stage) sink.error(r.row, field, 'CONTROL_STAGE_MISMATCH');
        else if (
          hazardCode !== null &&
          hazardsByCode.get(hazardCode)?.family !== control.hazardFamily
        ) {
          sink.error(r.row, field, 'LINK_MISMATCH');
        }
      }
      if (code === null || hazardCode === null) continue;
      // Hatalı bağ da tutulur: 05 ile tehlike karşılaştırması yapılır, satır yine reddedilir
      relations.set(code, { row: r.row, hazardCode, existing, additional });
      if (failed.has(r.row)) failedRelations.add(code);
    }
  }

  // 05 RA Import → faaliyet–tehlike bağı (06 ile)
  {
    const failed = new Set<number>();
    const sink = sinkFor('rows', failed);
    const seen = new Set<string>();
    const rowCodes = new Set<string>();
    for (const r of raRows) {
      const code = checkCode(sink, r.row, r.text('code'), seen);
      if (code !== null) rowCodes.add(code);
      const activityCode = codeOf(r.text('activityCode'));
      const activity = activityCode === null ? undefined : activities.get(activityCode);
      // Faaliyet dosyada yoksa var olan ana veride aranır (db adımı); burada yalnız biçim
      if (activityCode === null) sink.error(r.row, 'activityCode', 'REQUIRED');
      const family = codeOf(r.text('family'));
      const hazard = family === null ? undefined : hazardsByFamily.get(family);
      if (family === null) sink.error(r.row, 'family', 'REQUIRED');
      else if (!hazard) sink.error(r.row, 'family', 'UNKNOWN_HAZARD');
      const likelihood = scaleOf(r.text('likelihood'));
      const severity = scaleOf(r.text('severity'));
      if (likelihood === null || likelihood === undefined) {
        sink.error(r.row, 'likelihood', likelihood === null ? 'REQUIRED' : 'INVALID_SCALE');
      }
      if (severity === null || severity === undefined) {
        sink.error(r.row, 'severity', severity === null ? 'REQUIRED' : 'INVALID_SCALE');
      }
      // Skor = L × S; tutmazsa satır reddedilir, düzeltilmez (XL 09 "Initial score")
      const initialScore = r.text('initialScore');
      if (
        typeof likelihood === 'number' &&
        typeof severity === 'number' &&
        initialScore !== null &&
        Number(initialScore) !== likelihood * severity
      ) {
        sink.error(r.row, 'initialScore', 'SCORE_MISMATCH');
      }
      const rl = scaleOf(r.text('residualLikelihood'));
      const rs = scaleOf(r.text('residualSeverity'));
      if (rl === undefined) sink.error(r.row, 'residualLikelihood', 'INVALID_SCALE');
      if (rs === undefined) sink.error(r.row, 'residualSeverity', 'INVALID_SCALE');
      const residualScore = r.text('residualScore');
      if (
        typeof rl === 'number' &&
        typeof rs === 'number' &&
        residualScore !== null &&
        Number(residualScore) !== rl * rs
      ) {
        sink.error(r.row, 'residualScore', 'SCORE_MISMATCH');
      }
      const controlStatus = controlStatusOf(r.text('controlStatus'));
      if (!controlStatus) sink.error(r.row, 'controlStatus', 'INVALID_CONTROL_STATUS');
      const evidence = r.text('evidence');
      if (!within(evidence, RA_LIBRARY_LIMITS.evidence)) sink.error(r.row, 'evidence', 'TOO_LONG');
      const exposed = r.text('exposed');
      if (!within(exposed, RA_LIBRARY_LIMITS.exposed)) sink.error(r.row, 'exposed', 'TOO_LONG');
      const relation = code === null ? undefined : relations.get(code);
      if (code !== null && !relation) sink.error(r.row, 'code', 'LINK_MISSING');
      if (relation && hazard && relation.hazardCode !== hazard.code) {
        errors.push({
          sheet: 'links',
          row: relation.row,
          field: 'hazardCode',
          code: 'LINK_MISMATCH',
        });
        failed.add(r.row);
      }
      if (code !== null && failedRelations.has(code)) failed.add(r.row);
      if (
        failed.has(r.row) ||
        code === null ||
        activityCode === null ||
        !hazard ||
        !relation ||
        typeof likelihood !== 'number' ||
        typeof severity !== 'number' ||
        rl === undefined ||
        rs === undefined ||
        !controlStatus
      ) {
        continue;
      }
      const issue = raLinkIssues({
        code,
        activityId: '',
        stepId: null,
        hazardId: '',
        likelihood,
        severity,
        residualLikelihood: rl,
        residualSeverity: rs,
        existingControlIds: [],
        additionalControlIds: [],
        controlStatus,
        evidence: null,
        exposed: null,
        status: 'draft',
      })[0];
      if (issue) {
        sink.error(r.row, 'residualLikelihood', issue as RaLibraryIssueCode);
        continue;
      }
      // Metin farkları uyarıdır: bağ kimliklerden gelir (README §3.3)
      if (activity && r.text('activity') !== null && !sameText(r.text('activity'), activity.name)) {
        warn('rows', r.row, 'activity', 'TEXT_MISMATCH');
      }
      if (r.text('hazard') !== null && !sameText(r.text('hazard'), hazard.name)) {
        warn('rows', r.row, 'hazard', 'TEXT_MISMATCH');
      }
      const existingMeasure = relation.existing
        ? (controlsByCode.get(relation.existing)?.measure ?? null)
        : null;
      const additionalMeasure = relation.additional
        ? (controlsByCode.get(relation.additional)?.measure ?? null)
        : null;
      if (r.text('existing') !== null && !sameText(r.text('existing'), existingMeasure)) {
        warn('rows', r.row, 'existing', 'TEXT_MISMATCH');
      }
      if (r.text('additional') !== null && !sameText(r.text('additional'), additionalMeasure)) {
        warn('rows', r.row, 'additional', 'TEXT_MISMATCH');
      }
      if (activity && r.text('task') !== null && !sameText(r.text('task'), activity.scope)) {
        warn('rows', r.row, 'task', 'STEP_MISMATCH');
      }
      if (activity && r.text('vesselGroups') !== null) {
        const groups = groupsOf(r.text('vesselGroups'));
        if (!groups || groups.join(',') !== activity.vesselGroups.join(',')) {
          warn('rows', r.row, 'vesselGroups', 'APPLICABILITY_MISMATCH');
        }
      }
      data.links.push({
        row: r.row,
        code,
        activityCode,
        hazardCode: hazard.code,
        stepText: r.text('task') ?? activity?.scope ?? null,
        likelihood,
        severity,
        residualLikelihood: rl,
        residualSeverity: rs,
        existingControlCode: relation.existing,
        additionalControlCode: relation.additional,
        controlStatus,
        evidence,
        exposed,
      });
    }
    // 06'da olup 05'te olmayan satır
    for (const [code, relation] of relations) {
      if (!rowCodes.has(code)) {
        errors.push({ sheet: 'links', row: relation.row, field: 'code', code: 'UNKNOWN_ROW' });
      }
    }
  }

  // 07 Matrix & Rules: bantlar (R-xx "Risk band")
  {
    const bands: RaMatrixBand[] = [];
    let invalid = false;
    for (const r of matrixRows) {
      if (!sameText(r.text('setting'), 'Risk band')) continue;
      const range = /^(\d+)\s*[–—-]\s*(\d+)$/.exec(r.text('value') ?? '');
      const key = bandKeyOf(r.text('meaning'));
      if (!range || !key) {
        errors.push({ sheet: 'matrix', row: r.row, field: 'value', code: 'MATRIX_INVALID' });
        invalid = true;
        continue;
      }
      bands.push({ key, min: Number(range[1]), max: Number(range[2]), ...acceptanceOf(key) });
    }
    if (bands.length > 0 && !invalid) {
      const parsed = raMatrixSchema.safeParse({ bands });
      if (!parsed.success || raMatrixIssues(parsed.data).length > 0) {
        const row = matrixRows.find((r) => sameText(r.text('setting'), 'Risk band'))?.row ?? 1;
        errors.push({ sheet: 'matrix', row, field: 'value', code: 'MATRIX_INVALID' });
      } else {
        data.matrix = parsed.data;
      }
    }
  }

  // Satırları dosya sırasıyla (sayfa, satır) sırala: rapor okunur kalır
  const order = Object.keys(RA_LIBRARY_SHEET_NAMES) as RaImportSheet[];
  const byPlace = (a: RaLibraryIssue, b: RaLibraryIssue) =>
    order.indexOf(a.sheet) - order.indexOf(b.sheet) || a.row - b.row;
  errors.sort(byPlace);
  warnings.sort(byPlace);
  return data;
}
